import { httpError } from "@/components/setup/http";
import apiClient from "@/lib/api-client";
import { useSetupStore } from "@/stores/setup-store";
import { type Band, chunk, TRIAGE_CHUNK, type TriagePaper } from "./lanes";

const MAX_PARALLEL = 3;
// `${qh}|${key}` for papers in a request now, so overlapping calls (the
// effect re-runs as networks land) never send a paper twice.
const inflight = new Set<string>();

/** Test hook. */
export function __resetTriage() { inflight.clear(); }

/** ②'s quick check for `papers` against question `qh`. Never rejects: results
 *  go in the setup store; a 429 marks the question "capped" (no more requests),
 *  any other error marks it "failed" until called again with retry. */
export async function ensureTriage(
  conceptId: string, qh: string, papers: TriagePaper[], { retry = false }: { retry?: boolean } = {},
): Promise<void> {
  const store = useSetupStore.getState();
  const err = store.triageError[qh];
  if (err === "capped" || (err === "failed" && !retry)) return;
  if (retry) store.setTriageError(qh, undefined);
  const done = store.triage[qh] ?? {};
  const todo = papers.filter((p) => !(p.key in done) && !inflight.has(`${qh}|${p.key}`));
  if (!todo.length) return;
  for (const p of todo) inflight.add(`${qh}|${p.key}`);
  const queue = chunk(todo, TRIAGE_CHUNK);
  let stop = false;
  const worker = async () => {
    for (let c = queue.shift(); c && !stop; c = queue.shift()) {
      try {
        const res = await apiClient
          .post(`api/concepts/${conceptId}/setup/triage`, { json: { papers: c }, timeout: 120_000 })
          .json<{ results: { key: string; band: Band }[] }>();
        useSetupStore.getState().setTriage(qh, c.map((p) => p.key), res.results);
      } catch (e) {
        const capped = (await httpError(e)).status === 429;
        useSetupStore.getState().setTriageError(qh, capped ? "capped" : "failed");
        if (capped) stop = true;
      } finally {
        for (const p of c) inflight.delete(`${qh}|${p.key}`);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, worker));
  for (const p of queue.flat()) inflight.delete(`${qh}|${p.key}`); // left unsent after a 429
}
